import React from 'react';
import { Loader2, Film, Users, MessageSquare, Package } from 'lucide-react';

// Base loading spinner
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  return <Loader2 className={`animate-spin text-red-600 ${sizeClasses[size]} ${className}`} />;
};

// Full page loading
export const PageLoading = ({ message = 'Loading...' }) => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <LoadingSpinner size="xl" />
      <p className="mt-4 text-white text-lg">{message}</p>
    </div>
  </div>
);

// Card/component loading skeleton
export const LoadingSkeleton = ({ className = '', children }) => (
  <div className={`animate-pulse ${className}`}>{children}</div>
);

// Movie card loading skeleton
export const MovieCardSkeleton = () => (
  <LoadingSkeleton>
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="h-64 bg-gray-200"></div>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        <div className="flex space-x-2">
          <div className="h-6 bg-gray-200 rounded w-16"></div>
          <div className="h-6 bg-gray-200 rounded w-12"></div>
        </div>
      </div>
    </div>
  </LoadingSkeleton>
);

// Modal loading content
export const ModalLoading = ({ message = 'Loading...' }) => (
  <div className="flex items-center justify-center py-12">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600">{message}</p>
    </div>
  </div>
);

// Button loading state
export const LoadingButton = ({
  isLoading,
  children,
  className = '',
  disabled = false,
  ...props
}) => (
  <button
    disabled={isLoading || disabled}
    className={`relative ${isLoading ? 'cursor-not-allowed opacity-70' : ''} ${className}`}
    {...props}
  >
    {isLoading && (
      <div className="absolute inset-0 flex items-center justify-center">
        <LoadingSpinner size="sm" className="text-current" />
      </div>
    )}
    <span className={isLoading ? 'invisible' : 'visible'}>{children}</span>
  </button>
);

// List loading with skeletons
export const ListLoadingSkeleton = ({ count = 3, itemHeight = 'h-16' }) => (
  <div className="space-y-3">
    {Array.from({ length: count }, (_, i) => (
      <LoadingSkeleton key={i} className={`bg-gray-200 rounded-lg ${itemHeight} w-full`} />
    ))}
  </div>
);

// Contextual loading components for different sections
export const CollectionLoading = () => (
  <div className="text-center py-12">
    <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
    <LoadingSpinner size="lg" />
    <p className="mt-4 text-gray-600">Loading your collection...</p>
  </div>
);

export const CommunityLoading = () => (
  <div className="text-center py-12">
    <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
    <LoadingSpinner size="lg" />
    <p className="mt-4 text-gray-600">Loading community data...</p>
  </div>
);

export const ForumLoading = () => (
  <div className="text-center py-12">
    <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
    <LoadingSpinner size="lg" />
    <p className="mt-4 text-gray-600">Loading forum posts...</p>
  </div>
);

export const MovieSearchLoading = () => (
  <div className="text-center py-8">
    <Film className="mx-auto h-10 w-10 text-gray-400 mb-3" />
    <LoadingSpinner size="md" />
    <p className="mt-3 text-gray-600 text-sm">Searching movies...</p>
  </div>
);

// Image loading placeholder
export const ImageLoading = ({ className = '', alt = '' }) => (
  <div className={`bg-gray-200 animate-pulse flex items-center justify-center ${className}`}>
    <Film className="h-8 w-8 text-gray-400" />
    <span className="sr-only">Loading image: {alt}</span>
  </div>
);

// Table loading skeleton
export const TableLoadingSkeleton = ({ columns = 4, rows = 5 }) => (
  <div className="space-y-2">
    {/* Header */}
    <LoadingSkeleton>
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array.from({ length: columns }, (_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
    </LoadingSkeleton>

    {/* Rows */}
    {Array.from({ length: rows }, (_, rowIndex) => (
      <LoadingSkeleton key={rowIndex}>
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }, (_, colIndex) => (
            <div key={colIndex} className="h-3 bg-gray-200 rounded"></div>
          ))}
        </div>
      </LoadingSkeleton>
    ))}
  </div>
);

// Higher-order component to add loading states
export const withLoading = (Component, LoadingComponent = LoadingSpinner) => {
  const WrappedComponent = ({ isLoading, ...props }) => {
    if (isLoading) {
      return <LoadingComponent />;
    }
    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withLoading(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for managing loading states
export const useLoading = (initialState = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);

  const startLoading = React.useCallback(() => setIsLoading(true), []);
  const stopLoading = React.useCallback(() => setIsLoading(false), []);
  const toggleLoading = React.useCallback(() => setIsLoading(prev => !prev), []);

  // For async operations
  const withLoadingAsync = React.useCallback(
    async asyncFn => {
      startLoading();
      try {
        const result = await asyncFn();
        return result;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading]
  );

  return {
    isLoading,
    startLoading,
    stopLoading,
    toggleLoading,
    withLoadingAsync,
  };
};

const LoadingComponents = {
  PageLoading,
  LoadingSpinner,
  LoadingSkeleton,
  MovieCardSkeleton,
  ModalLoading,
  LoadingButton,
  ListLoadingSkeleton,
  CollectionLoading,
  CommunityLoading,
  ForumLoading,
  MovieSearchLoading,
  ImageLoading,
  TableLoadingSkeleton,
  withLoading,
  useLoading,
};

export default LoadingComponents;
